import type { ChildGuideItem } from '@tunein/web-api-client';
import type { LoadOptions } from '@tunein/web-player-manager';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { discordManager } from 'src/common/components/discord';
import { selectDiscordState, selectIsMobile } from 'src/common/selectors/app';
import { getClientAppServices } from 'src/common/services';
import { type PlayerSingletonOptions, player } from './player';

/**
 * Discord is a shared experience, but with the option to only allow the host to control playback.
 *
 * This DiscordPlayer extends the player singleton by:
 *
 * 1. Overloading player singleton methods with a layer of checks that prevent non-hosts from controlling playback when
 *  they're not allowed to.
 * 2. `discordSync*` methods that allow DiscordManager to synchronize playback with the host, even if the user isn't
 *  able to control playback directly (per point 1 above)
 *
 *  We're able to achieve export-compatibility with the player singleton by:
 *  1. Using a Proxy to intercept method calls and route them to the player singleton instance, when appropriate
 *  2. In index.js, we conditionally export the appropriate player (discordPlayer or player singleton) as a `player`
 *    const, relying on `isInDiscordIframe()`.
 */
export class DiscordPlayer {
  static proxyHandlers: ProxyHandler<DiscordPlayer> = {
    get(target: DiscordPlayer, prop: string | symbol, receiver: any): any {
      // Check if the property exists on DiscordPlayer singleton
      if (prop in target) {
        return Reflect.get(target, prop, receiver);
      }

      if (prop in player) {
        // Otherwise, use property from player singleton
        return player[prop as keyof typeof player];
      }

      // Otherwise, use property from player singleton instance
      if (prop in player.instance) {
        const matchedProp = player.instance[prop as keyof typeof player.instance];

        return typeof matchedProp === 'function' ? matchedProp.bind(player.instance) : matchedProp;
      }

      return undefined;
    },
    set(target: DiscordPlayer, prop: string | symbol, value: any, receiver: any): boolean {
      // Check if the property exists on DiscordPlayer singleton
      if (prop in target) {
        return Reflect.set(target, prop, value, receiver);
      }

      // Otherwise, use property from player singleton
      if (prop in player) {
        (player as any)[prop] = value;
        return true;
      }

      // Otherwise, use property from player singleton instance
      if (prop in player.instance) {
        (player.instance as any)[prop] = value;
        return true;
      }

      return false;
    },
  };

  init(options: PlayerSingletonOptions) {
    const defaultVolumeForDiscord = 0.2;
    const defaultMobileVolumeForDiscord = 0.5;
    const isMobile = selectIsMobile(storeSingleton.state);

    const defaultVolume: number = isMobile
      ? defaultMobileVolumeForDiscord
      : defaultVolumeForDiscord;

    player.init({
      ...options,
      playerOptions: {
        ...options.playerOptions,
        volume: defaultVolume,
      },
    });
  }

  onClick = async (guideItem?: ChildGuideItem, options?: LoadOptions) => {
    if (this.#shouldBlockControlForDiscordUser) {
      return;
    }

    return player.onClick(guideItem, options);
  };

  seekTo = (seekTime: number) => {
    if (this.#shouldBlockControlForDiscordUser) {
      return;
    }

    this.#maybeBroadcastDiscordState();

    player.seekTo(seekTime);
  };

  seekBackward = (seekOffset: number) => {
    if (this.#shouldBlockControlForDiscordUser) {
      return;
    }

    this.#maybeBroadcastDiscordState();

    player.seekBackward(seekOffset);
  };

  seekForward = (seekOffset: number) => {
    if (this.#shouldBlockControlForDiscordUser) {
      return;
    }

    this.#maybeBroadcastDiscordState();

    player.seekForward(seekOffset);
  };

  /**
   * In non-Discord modes the `canControlPlayback` flag is set to true by default for simplicity.
   */
  get #shouldBlockControlForDiscordUser() {
    const { canControlPlayback } = selectDiscordState(storeSingleton.state);

    return !canControlPlayback;
  }

  /**
   * Broadcasts Discord listening state to GraphQL Subscription Gateway when user-initiated seek is committed
   */
  #maybeBroadcastDiscordState() {
    if (discordManager.canControlPlayback) {
      player.once('seeked', discordManager.setListeningPosition);
    }
  }

  // SYNC METHODS BELOW THIS LINE
  // IMPORTANT: always reference `instance` to avoid accidentally invoking any of the player singleton override methods

  // TODO: review if it's ok that we're circumventing all side effects in `player.onClick`
  async discordSyncLoadAndPlay(guideItem: ChildGuideItem, options: LoadOptions) {
    const { playerStore } = getClientAppServices().stores;
    const { setCurrentGuideItem } = playerStore.getState();

    setCurrentGuideItem(guideItem);
    await player.instance.load(guideItem, { ...options, isRemotelyInitiated: true });

    if (player.instance.isLoaded()) {
      await player.instance.play();
    }

    // Alternatively, passthrough to onClick
    // return player.onClick(guideItem, options);
  }

  discordSyncSeekTo(seekTime: number) {
    player.instance.seekTo(seekTime);
  }

  discordSyncPlay() {
    return player.instance.play();
  }

  discordSyncPause() {
    player.instance.pause();
  }

  discordSyncStop() {
    player.instance.stop();
  }

  discordSyncSetPlaybackRate(playbackRate: number) {
    player.instance.setPlaybackRate(playbackRate);
  }
}

export const discordPlayer = new Proxy(new DiscordPlayer(), DiscordPlayer.proxyHandlers);
